import { createClient } from "@supabase/supabase-js";
import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

import type { Database } from "@/integrations/supabase/types";
import { getRuntimeEnv } from "@/lib/runtime-env";

const FALLBACK_URL = "https://xelbsokfryzjsagomcmg.supabase.co";
const FALLBACK_PUBLISHABLE_KEY = "sb_publishable_ID1Vn-HGIqXOQEde1QQSNg_eqJmQgd9";

function readServerValue(name: string): string | undefined {
  const runtimeValue = getRuntimeEnv()[name];
  if (typeof runtimeValue === "string" && runtimeValue) return runtimeValue;
  const processValue = process.env[name];
  return processValue || undefined;
}

/** Auth middleware that also works in the GitHub-built Cloudflare Worker. */
export const requireSupabaseAuth = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const request = getRequest();
    const authHeader = request.headers.get("authorization");
    if (!authHeader?.startsWith("Bearer ")) {
      throw new Error("Unauthorized: No bearer token provided");
    }

    const token = authHeader.slice("Bearer ".length);
    if (!token) throw new Error("Unauthorized: No token provided");

    const supabase = createClient<Database>(
      readServerValue("SUPABASE_URL") ?? FALLBACK_URL,
      readServerValue("SUPABASE_PUBLISHABLE_KEY") ?? FALLBACK_PUBLISHABLE_KEY,
      {
        global: { headers: { Authorization: `Bearer ${token}` } },
        auth: {
          storage: undefined,
          persistSession: false,
          autoRefreshToken: false,
        },
      },
    );

    const { data, error } = await supabase.auth.getClaims(token);
    const userId = data?.claims?.sub;
    if (error || typeof userId !== "string" || !userId) {
      throw new Error("Unauthorized: Invalid token");
    }

    return next({
      context: { supabase, userId, claims: data.claims },
    });
  },
);